'use client'

import cx from 'classnames'
import Image from 'next/image'
import { FC, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Swiper, SwiperSlide } from 'swiper/react'

import '@/components/About/Organisms/JournalSection/JournalSection.scss'
import Heading from '@/components/Heading'
import LinkButtonArrow from '@/components/LinkButtonArrow'
import { JournalContent } from '@/components/Page/Journals/JournalPage'
import Number from '@/components/Page/Page/Journal/Number'
import { getJournals } from '@/serverActions/getJournals'

type ItemProps = {
  date: string
  category: string
  title: string
  titleEn: string
  href: string
  number: string
  src: any
}
const JournalCard: FC<ItemProps> = ({ date, category, title, titleEn, number, href, src }) => {
  const { inView, ref } = useInView({
    threshold: 0,
    rootMargin: '0px 0px -20% 0px',
    triggerOnce: true,
  })

  return (
    <a
      className={cx('p-about-journal-card', 'u-mo-lineBox', {
        'p-about-journal-card--inView': inView,
      })}
      href={href}
      ref={ref}
    >
      <div className="p-about-journal-card-image">
        <Image src={src.src} width={src.width} height={src.height} alt={title} />
      </div>
      <div className="p-about-journal-card-info">
        <div className="p-about-journal-card-top">
          <p className="p-about-journal-card-category">{category}</p>
          <span className="p-about-journal-card-border"></span>
          <p className="p-about-journal-card-date">{date}</p>
        </div>

        <div className="p-about-journal-card-heading">
          <div className="p-about-journal-card-heading--num">
            <Number number={number} />
          </div>
          <div className="p-about-journal-card-title">
            <p className="p-about-journal-card-title-ja u-font-yu-min">
              <span className="u-mo-rowsView__text u-mo-lineBox__text">{title}</span>
            </p>
            <p className="p-about-journal-card-title-en u-font-gb">{titleEn}</p>
          </div>
        </div>
      </div>
    </a>
  )
}

const JournalSection = ({ title = 'Journal' }: { title?: string }) => {
  const [journalContents, setJournalContents] = useState<JournalContent[]>([])
  useEffect(() => {
    ;(async () => setJournalContents(await getJournals({})))()
  }, [])

  return (
    <section className="p-about-journal">
      <div className="p-about-journal__container">
        <div className="p-about-journal__heading">
          <Heading fontFamily="Goldenbook">{title}</Heading>
          <LinkButtonArrow href="/journals">View All</LinkButtonArrow>
        </div>

        <div className="p-about-journal__slider">
          <Swiper
            speed={580}
            spaceBetween={30}
            slidesPerView={1}
            className="p-about-journal__slider--swiper"
            grabCursor={false}
            breakpoints={{
              900: {
                slidesPerView: 2,
                grabCursor: true,
              },
            }}
          >
            {journalContents.map((journalContent) => {
              return (
                <SwiperSlide key={journalContent.number} className="p-about-journal__slider--swiper-slide">
                  <JournalCard
                    href={`/journals/${journalContent.number}`}
                    date={journalContent.publishedAt}
                    category={journalContent.category}
                    title={journalContent.title}
                    titleEn={journalContent.description}
                    number={journalContent.number}
                    src={journalContent.headerImage}
                  />
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
      </div>
    </section>
  )
}

export default JournalSection
